import { Position, Polygon, Feature, FeatureCollection, Geometry } from 'geojson';


const X = 0;
const Y = 1;
const Z = 2;

export function pointsToPolygon(pts: Position[]): Polygon {
  return {
    type: "Polygon",
    coordinates: [pts],
  }
}

export function pointsToFeature(pts: Position[]): Feature {
  const feature: Feature = {
    type: "Feature",
    geometry: pointsToPolygon(pts),
    properties: {},
  };
  polygonSetBoundingBox(feature);
  return feature;
}

export function isPolygon(feature: Feature) {
  return feature.type == "Feature" && feature.geometry.type == "Polygon";
}

export function polygonSetBoundingBox(feature: Feature) {
  if (!isPolygon(feature))
    return;
  const geometry: Polygon = feature.geometry as Polygon;
  const pts = geometry.coordinates[0];
  const xv = pts.map((p: Position) => p[X]);
  const yv = pts.map((p: Position) => p[Y]);
  if (feature.bbox)
    return
  feature.bbox = [
    Math.min(...xv),
    Math.min(...yv),
    Math.max(...xv),
    Math.max(...yv)
  ]
}

function peq(a: Position, b: Position) {
  return a[X] == b[X] && a[Y] == b[Y];
}

function btw(x: number, v0: number, v1: number) {
  return x >= v0 && x <= v1;
}

function point_on_line_segment(p: Position, a: Position[]) {
  const x1 = a[0][X], x2 = a[1][X];
  const y1 = a[0][Y], y2 = a[1][Y];
  const x3 = p[X];
  const y3 = p[Y];
  let s3y = undefined;
  let s3x = undefined;
  if (Math.abs(y2 - y1) > 1e-7) {
    s3y = (y3 - y1) / (y2 - y1);
  } else {
    s3y = y1 - y3;
  }
  if (Math.abs(x2 - x1) > 1e-7) {
    s3x = (x3 - x1) / (y2 - y1);
  } else {
    s3x = x1 - x3;
  }
  return btw(s3x, 0, 1) && btw(s3y, 0, 1);
}

function point_on_line_segment_where(p: Position, a: Position[]) {
  const x1 = a[0][X];
  const x2 = a[1][X];
  const y1 = a[0][Y];
  const y2 = a[1][Y];
  const x = p[X];
  const y = p[Y];
  const xv = [x1, x2].sort()
  const yv = [y1, y2].sort();
  if (!(p[X] >= xv[0] && p[X] <= xv[1]))
    return false
  if (!(p[Y] >= yv[0] && p[Y] <= yv[1]))
    return false;
  const m1 = (x2 - x1) * (y - y1)
  const m2 = (x - x1) * (y2 - y1)
  if (m1 != m2)
    return false
  // x = x1 + (x2-x1) * t (or the same for t
  // t = (x - x1) / (x2-x1)
  if (x2 != x1)
    return (x - x1) / (x2 - x1);
  return (y - y1) / (y2 - y1);
}

function line_segment_intersection_where(a: Position[], b: Position[]) {
  const ae = peq(a[0], a[1]);
  const be = peq(b[0], b[1]);
  if (ae || be) {
    if (ae && be) {
      if (peq(a[0], b[0])) {
        return 0;
      }
      return false;
    } else if (peq(a[0], a[1])) {
      return point_on_line_segment_where(a[0], b);
    }
    return point_on_line_segment_where(b[0], a);
  }
  if (peq(a[0], b[0]) || peq(a[0], b[1]) || peq(a[1], b[0]) || peq(a[1], b[1])) {
    console.log(`Line segments share two points`);
    return true;
  }
  const x1 = a[0][X], x2 = a[1][X], x3 = b[0][X], x4 = b[1][X];
  const y1 = a[0][Y], y2 = a[1][Y], y3 = b[0][Y], y4 = b[1][Y];
  const a1 = x2 - x1;
  const b1 = -(x4 - x3);
  const c1 = x3 - x1;
  const a2 = y2 - y1;
  const b2 = -(y4 - y3);
  const c2 = y3 - y1;
  const denom = a1 * b2 - a2 * b1;
  if (Math.abs(denom) <= 1e-7) { // Line are parallel
    let t: number | boolean = 0;
    t = point_on_line_segment_where(b[0], a);
    if (t !== false)
      return t;
    t = point_on_line_segment_where(b[1], a);
    if (t !== false)
      return 1 - t;
    t = point_on_line_segment_where(a[0], b);
    if (t !== false)
      return 0;
    t = point_on_line_segment_where(a[1], b);
    if (t !== false)
      return 1;
    return false;
  }
  const s = (c1 * b2 - c2 * b1) / denom;
  if (btw(s, 0, 1))
    return s;
  return false;

}

export function line_segment_intersection(a: Position[], b: Position[]) {
  const ae = peq(a[0], a[1]);
  const be = peq(b[0], b[1]);
  if (ae || be) {
    if (ae && be)
      return peq(a[0], b[0]);
    else if (peq(a[0], a[1]))
      return point_on_line_segment(a[0], b);
    return point_on_line_segment(b[0], a);
  }
  if (peq(a[0], b[0]) || peq(a[0], b[1]) || peq(a[1], b[0]) || peq(a[1], b[1])) {
    console.log(`Line segments share two points`);
    return true;
  }
  const x1 = a[0][X], x2 = a[1][X], x3 = b[0][X], x4 = b[1][X];
  const y1 = a[0][Y], y2 = a[1][Y], y3 = b[0][Y], y4 = b[1][Y];
  const a1 = x2 - x1;
  const b1 = -(x4 - x3);
  const c1 = x3 - x1;
  const a2 = y2 - y1;
  const b2 = -(y4 - y3);
  const c2 = y3 - y1;
  const denom = a1 * b2 - a2 * b1;
  if (Math.abs(denom) <= 1e-7) {
    let s3x = undefined;
    let s4x = undefined;
    let s3y = undefined;
    let s4y = undefined;
    if (Math.abs(y2 - y1) > 1e-7) {
      s3y = (y3 - y1) / (y2 - y1);
      s4y = (y4 - y1) / (y2 - y1);
    } else {
      s3y = s4y = y1 - y3;
    }
    if (Math.abs(x2 - x1) > 1e-7) {
      s3x = (x3 - x1) / (x2 - x1);
      s4x = (x4 - x1) / (x2 - x1);
    } else {
      s3x = s4x = x1 - x3;
    }
    return (btw(s3x, 0, 1) && btw(s3y, 0, 1)) || (btw(s4x, 0, 1) && btw(s4y, 0, 1))
  }
  const s = (c1 * b2 - c2 * b1) / denom;
  const t = (a1 * c2 - a2 * c1) / denom;
  //console.log(s, t)
  return (s >= 0 && t >= 0 && s <= 1 && t <= 1);
}

export function polygonIsSimple(feature: Feature) {
  if (feature.type != "Feature")
    return false;
  if (feature.geometry.type != "Polygon")
    return false
  return is_simple_polygon(feature.geometry.coordinates[0])
}

function is_simple_polygon(pts: Position[]) {
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    let a = pts.slice(i, i + 2); // line segment to test
    for (let j = i + 2; j < n; j++) {
      if ((j + 1) % n == i)
        continue
      let b = pts.slice(j, j + 2);
      if (j + 1 >= n) {
        b = [pts[j], pts[0]]
      }
      //console.log(i, i + 1, j, j + 1)

      if (line_segment_intersection(a, b)) {
        return false;
      }
    }
  }
  return true;
}

// Check is a point (x,y,z) is contained within a polygon's bounding box
//   Bounding box is defined in bbox (xmin, ymin, xmax, ymax)
function isPointInsideBoundingBox(poly: Feature, pt: Position): boolean {
  const bbox = poly.bbox;
  if (!bbox || bbox.length != 4)
    return true;
  return (
    pt[0] >= bbox[0] &&
    pt[1] >= bbox[1] &&
    pt[0] <= bbox[2] &&
    pt[1] <= bbox[3]
  );
}

// Check is a point (x,y,z) is contained within a polygon
//   The bounding box is first checked then the polygon is checked
export function isPointInsidePolygon(poly: Feature, pt: Position): boolean {
  const geom = poly.geometry as Polygon;
  return isPointInsideBoundingBox(poly, pt) && isPointInsidePolygonRCA(pt, geom.coordinates[0]);
}

// Check if a point is within a polygon (pts)
//  https://en.wikipedia.org/wiki/Point_in_polygon#Ray_casting_algorithm
function isPointInsidePolygonRCA(point: Position, pts: Position[]) {
  let n = pts.length;
  let xp = point[0];
  let yp = point[1];
  let xv: any = pts.map((p: any) => p[0]);
  let yv: any = pts.map((p: any) => p[1]);
  if (Math.abs(xv[0] - xv[n - 1]) < 1e-7 && Math.abs(yv[0] - yv[n - 1]) < 1e-7) {
    n -= 1;
  }
  let x2 = xv[n - 1]
  let y2 = yv[n - 1]
  let nleft = 0

  let x1 = x2;
  let y1 = y2;

  // Loop over line segments (assuming the polygon is closed)
  for (let i = 0; i < n; i++) {
    x1 = x2
    y1 = y2
    x2 = xv[i]
    y2 = yv[i]
    if (y1 >= yp && y2 >= yp) {
      continue;
    }
    if (y1 < yp && y2 < yp) {
      continue;
    }
    if (y1 == y2) {
      if (x1 >= xp && x2 >= xp) {
        continue;
      }
      if (x1 < xp && x2 < xp) {
        continue;
      }
      nleft += 1;
    } else {
      let xi = x1 + (yp - y1) * (x2 - x1) / (y2 - y1);
      if (xi == xp) {
        nleft = 1;
        break;
      }
      if (xi > xp) {
        nleft += 1;
      }
    }
  }
  let xin = nleft % 2;
  return xin == 1;
}

// Test all polygons (polys) if a point lies in any
//    polygons are assumed to be in GeoJSON format and have:
//      - a priority where overlapping polygons with higher priority are chosen
//    Returns all found polygons or [] in the case of no match
export function findPolygon(p: Position, polys: FeatureCollection) {
  let found = [];
  for (let j = 0; j < polys.features.length; j++) {
    const poly = polys.features[j];
    if (isPointInsidePolygon(poly, p)) {
      found.push(j);
    }
  }
  return found;
}

function vlen2(p: Position) {
  return p[0] * p[0] + p[1] * p[1]
}
function vlen(p: Position) {
  return Math.sqrt(vlen2(p))
}

function pointLineDist(p: Position, p1: Position, p2: Position) {
  const x1 = p1[0];
  const y1 = p1[1];
  const x2 = p2[0];
  const y2 = p2[1];
  const x0 = p[0];
  const y0 = p[2];
  const dx = x2 - x1;
  const dy = y2 - y1;
  const numer = Math.abs(dx * (y1 - y0) - (x1 - x0) * dy);
  const denom = Math.sqrt(dx * dx + dy * dy);
  return numer / denom;
}

function sqr(x: number) { return x * x }
function dist2(v: Position, w: Position) { return sqr(v[X] - w[X]) + sqr(v[Y] - w[Y]); }
function distToSegmentSquared(p: Position, v: Position, w: Position) {
  var l2 = dist2(v, w);
  if (l2 == 0) return dist2(p, v);
  var t = ((p[X] - v[X]) * (w[X] - v[X]) + (p[Y] - v[Y]) * (w[Y] - v[Y])) / l2;
  t = Math.max(0, Math.min(1, t));
  return dist2(p, [
    v[X] + t * (w[X] - v[X]),
    v[Y] + t * (w[Y] - v[Y])
  ]);
}
function distToSegment(p: Position, v: Position, w: Position) {
  return Math.sqrt(distToSegmentSquared(p, v, w))
}

function distToPolygon(p: Position, poly: Feature) {
  let minDist = 1e7;
  let geom: Polygon = poly.geometry as Polygon;
  const n = geom.coordinates[0].length;
  for (let i = 0; i < n; i++) {
    const p1 = geom.coordinates[0][i];
    const p2 = geom.coordinates[0][(i + 1) % n];
    const dist = distToSegment(p, p1, p2);
    if (dist < minDist) {
      minDist = dist;
    }
  }
  return minDist;

}

export function findPolygons(p: Position, polys: FeatureCollection, minDist: number) {
  let out = findPolygon(p, polys);
  if (out.length > 0) {
    return out;
  }
  for (let j = 0; j < polys.features.length; j++) {
    const poly = polys.features[j];
    const dist = distToPolygon(p, poly);
    if (dist < minDist) {
      out.push(j)
    }
  }
  return out;
}

function line_side(p: Position, line: Position[]) {
  const x = p[X];
  const y = p[Y];
  const x1 = line[0][X];
  const y1 = line[0][Y];
  const x2 = line[1][X];
  const y2 = line[1][Y];
  const d = (x - x1) * (y2 - y1) - (y - y1) * (x2 - x1);
  return d;
}

function getPointOnLine(a: Position[], t: number) {
  const x = a[0][X] + (a[1][X] - a[0][X]) * t;
  const y = a[0][Y] + (a[1][Y] - a[0][Y]) * t;
  return [x, y];
}

function polygonOrientation(pts: Position[]) {
  //const pts = (poly.geometry as Polygon).coordinates[0];
  let j = 0;
  for (let i = 1; i < pts.length; i++) {
    if (pts[i][X] < pts[j][X]) {
      j = i;
    }
    if (pts[i][X] == pts[j][X] && pts[i][Y] < pts[j][Y]) {
      j = i;
    }
  }
  let n = pts.length;
  let i = (j == 0) ? (n - 1) : (j - 1);
  let k = (j + 1) % n;
  const x1 = pts[i][X];
  const x2 = pts[j][X];
  const x3 = pts[k][X];
  const y1 = pts[i][Y];
  const y2 = pts[j][Y];
  const y3 = pts[k][Y];
  const det = (x2 - x1) * (y3 - y1) - (x3 - x1) * (y2 - y1);
  //console.log('det', x1, x2, x3)
  //console.log('det', y1, y2, y3)
  return det > 0;
}

export function polygonSplit(poly: Feature, line: Position[]) {
  let side = [];
  let pts = [];
  const pts0 = (poly.geometry as Polygon).coordinates[0];
  const orientation = polygonOrientation(pts0);

  let n = pts0.length;
  for (let i = 0; i < pts0.length; i++) {
    let a: Position[] = pts0.slice(i, i + 2);
    if (i + 1 >= n)
      a = [pts0[i], pts0[0]];

    //console.log(a, i, i + 1, line)
    const d1 = line_side(a[0], line);
    const d2 = line_side(a[1], line);
    //console.log(d1, d2);
    if (d1 == 0) { // Starting Point on line
      pts.push(a[0]);
      side.push(0);
    } else if (d1 * d2 < 0 && d2 != 0) { // Crosses line
      const t = line_segment_intersection_where(a, line);
      if (t === false) {
        console.error("Error splitting polygon on line segment intersection");
        return undefined;
      }
      const p = getPointOnLine(a, t as number);
      pts.push(a[0])
      side.push(d1 / Math.abs(d1));
      pts.push(p)
      side.push(0);
    } else {
      pts.push(a[0]);
      side.push(d1 / Math.abs(d1))
    }
  }
  //for (let i = 0; i < pts.length; i++) {
  //  console.log(side[i], pts[i]);
  //}
  const out = [];
  n = pts.length;
  for (let s = -1; s <= 1; s += 2) {
    //console.log("Working on side: ", s);
    let active = [];
    let is_pair = false;
    let part: any = [];
    let is_part = false;
    for (let i = 0; i < pts.length; i++) {
      let k = (i + 1) % n;
      let j = (i == 0) ? n - 1 : i - 1;
      if (side[i] == s) {
        //console.log("pt on ", s, "adding ", pts[i])
        if (active.length == 0) {
          //console.log("   first point for poly (not source)")
          is_pair = false;
          is_part = true;
        }
        active.push(pts[i]);
      } else if (side[i] == 0) {
        //console.log("pt on line adding", pts[i]);
        if (active.length == 0) {
          //console.log("   first point for poly (source)")
          is_pair = true;
        }
        active.push(pts[i]);
        if (side[j] != side[k] && active.length >= 3) { // Crossed line
          const ori = polygonOrientation(active);
          //console.log("   crossed the line active.length, is_pair", active.length, is_pair, ori, orientation);
          if (is_pair && ori == orientation) {
            //console.log("   adding as a polygon");
            out.push(active);
            active = [];
          } else if (is_part) {
            part = [...active];
            is_part = false;
            active = [];
          }
        }
      } else {
        //console.log("pt on ", s, "skipping", pts[i])
      }
    }
    if (active.length) {
      if (part.length) {
        part.push(...active)
        active = part;
      }
      out.push(active);
    }
  }
  return out;
}


